import { StreamElementType } from 'communication/types/StreamElementType';
import { LoggingService } from '@instinet-analytics/core-lib';
import { buildLogger, Logger } from '@instinet-analytics/core-lib';
import IntervalBuffer from 'utils/IntervalBuffer';
import LiquiditySummaryDataMessage from 'widgets/liquiditySummary/api/LiquiditySummaryDataMessage';
import { LiquiditySummaryStreamElementType } from 'widgets/liquiditySummary/api/LiquiditySummaryStreamElementType';
import { LiquiditySummaryElement } from 'widgets/liquiditySummary/api/LiquiditySummaryElement';

const ClearMessage: LiquiditySummaryDataMessage[] = [
    { data: [{ _c: StreamElementType.Clear }] },
];

interface LiquiditySummaryMessageBufferInitialisationStatus {
    infoReceived: boolean;
    volumeReceived: boolean;
    predRemVolReceived: boolean;
    predTradVolReceived: boolean;
}

const initInitialisationStatus: LiquiditySummaryMessageBufferInitialisationStatus =
    {
        infoReceived: false,
        volumeReceived: false,
        predRemVolReceived: false,
        predTradVolReceived: false,
    };

const isInitialised = (
    initStatus: LiquiditySummaryMessageBufferInitialisationStatus
) =>
    initStatus.infoReceived &&
    initStatus.volumeReceived &&
    initStatus.predRemVolReceived &&
    initStatus.predTradVolReceived;

/*
 * Buffers liquidity summary stream messages
 */
export default class LiquiditySummaryMessageBuffer {
    private buffer: IntervalBuffer<LiquiditySummaryDataMessage>;
    private initialisationStatus = { ...initInitialisationStatus };
    private logger: Logger;
    private streamKey = '';

    public constructor(
        private out: (
            streamKey: string,
            data: LiquiditySummaryDataMessage[]
        ) => void,
        continueInterval: () => boolean,
        loggingService: LoggingService
    ) {
        const outWrapper = (data: LiquiditySummaryDataMessage[]) =>
            out(this.streamKey, data);

        this.buffer = new IntervalBuffer(outWrapper, continueInterval);
        this.logger = buildLogger(
            'LiquiditySummaryMessageBuffer',
            loggingService
        );
    }

    public in(
        streamKey: string,
        data: LiquiditySummaryDataMessage[] | undefined
    ) {
        this.streamKey = streamKey;
        if (!data) {
            return;
        }

        if (this.isClear(data)) {
            // Message starts with a Clear, we kill the buffer and immediately dispatch a clear
            this.logger.debug('Clear received - clearing stream state');
            this.out(streamKey, ClearMessage);
            this.buffer.reset();
            this.buffer.setInterval(1000);
            // Set back to initialising...
            this.initialisationStatus = { ...initInitialisationStatus };
        }

        const initialising = !isInitialised(this.initialisationStatus);

        /*
         * Pass to buffer, suppressing immediate flushing of first message until initialised...
         */
        const suppressImmediateFlush = initialising;
        this.buffer.in(data, suppressImmediateFlush);

        if (this.completesInitialisation(data)) {
            this.logger.debug('Initialisation complete - flushing messages');
            this.buffer.flush();
            this.buffer.setInterval(1000);
        }
    }

    private isClear(data: LiquiditySummaryDataMessage[]): boolean {
        const elements = this.getElements(data);
        if (!elements) {
            return false;
        }

        return elements[0]._c === StreamElementType.Clear;
    }

    private completesInitialisation(
        data: LiquiditySummaryDataMessage[]
    ): boolean {
        const elements = this.getElements(data);
        if (!elements) {
            return false;
        }

        if (isInitialised(this.initialisationStatus)) {
            return false;
        }

        this.initialisationStatus.infoReceived =
            this.initialisationStatus.infoReceived ||
            this.includesElement(
                elements,
                LiquiditySummaryStreamElementType.Info
            );
        this.initialisationStatus.volumeReceived =
            this.initialisationStatus.volumeReceived ||
            this.includesElement(
                elements,
                LiquiditySummaryStreamElementType.Volume
            );
        this.initialisationStatus.predRemVolReceived =
            this.initialisationStatus.predRemVolReceived ||
            this.includesElement(
                elements,
                LiquiditySummaryStreamElementType.PredRemVol
            );
        this.initialisationStatus.predTradVolReceived =
            this.initialisationStatus.predTradVolReceived ||
            this.includesElement(
                elements,
                LiquiditySummaryStreamElementType.PredTradVol
            );

        return isInitialised(this.initialisationStatus);
    }

    private getElements(
        data: LiquiditySummaryDataMessage[]
    ): LiquiditySummaryElement[] | undefined {
        return data &&
            data.length > 0 &&
            data[0].data &&
            data[0].data.length > 0
            ? data[0].data
            : undefined;
    }

    private includesElement(
        elements: LiquiditySummaryElement[],
        elementType: LiquiditySummaryStreamElementType
    ): boolean {
        return !!elements.find((d) => d._c === elementType);
    }
}
